/**
 * AI Insights and Smart Features Service
 * Provides automated insights, duplicate detection, player categorization,
 * and intelligent suggestions for the scouting system
 */

class AIInsightsService {
  /**
   * 1. DUPLICATE PLAYER DETECTION
   * Detects potential duplicate players across multiple game reports
   */
  static detectDuplicatePlayers(allGameReports, currentPlayer) {
    const potentialDuplicates = [];
    const threshold = 0.7; // Similarity threshold (70%)

    // Get all players from all game reports
    const allPlayers = [];
    allGameReports.forEach((report) => {
      if (report.playersGameData && Array.isArray(report.playersGameData)) {
        report.playersGameData.forEach((player) => {
          allPlayers.push({
            ...player,
            gameId: report.id,
            gameDate: report.matchDate,
            venue: report.gameContext?.venue?.name || report.venue || "Unknown",
            homeTeam: report.homeTeam,
            awayTeam: report.awayTeam,
          });
        });
      }
    });

    // Compare current player with all existing players
    allPlayers.forEach((existingPlayer) => {
      const similarity = this.calculatePlayerSimilarity(
        currentPlayer,
        existingPlayer,
      );

      if (similarity >= threshold) {
        potentialDuplicates.push({
          player: existingPlayer,
          similarity: similarity,
          matchDetails: {
            date: existingPlayer.gameDate,
            venue: existingPlayer.venue,
            fixture: `${existingPlayer.homeTeam} vs ${existingPlayer.awayTeam}`,
          },
          confidenceLevel: this.getConfidenceLevel(similarity),
          suggestedAction: this.getSuggestedDuplicateAction(similarity),
        });
      }
    });

    return potentialDuplicates.sort((a, b) => b.similarity - a.similarity);
  }

  /**
   * Calculate similarity between two players
   */
  static calculatePlayerSimilarity(player1, player2) {
    let score = 0;
    let factors = 0;

    // Name similarity (weighted heavily)
    if (player1.name && player2.name) {
      const nameSimilarity = this.calculateStringSimilarity(
        player1.name.toLowerCase().trim(),
        player2.name.toLowerCase().trim(),
      );
      score += nameSimilarity * 0.4; // 40% weight
      factors += 0.4;
    }

    // Position match
    if (player1.position && player2.position) {
      const positionMatch =
        player1.position.toLowerCase() === player2.position.toLowerCase()
          ? 1
          : 0;
      score += positionMatch * 0.15; // 15% weight
      factors += 0.15;
    }

    // Team match
    if (player1.team && player2.team) {
      const teamMatch =
        player1.team.toLowerCase() === player2.team.toLowerCase() ? 1 : 0;
      score += teamMatch * 0.15; // 15% weight
      factors += 0.15;
    }

    // Phone number exact match (if available)
    if (player1.phone && player2.phone && player1.phone === player2.phone) {
      score += 0.3; // 30% weight for exact phone match
      factors += 0.3;
    }

    // Rating similarity (lower weight)
    if (this.hasValidRatings(player1) && this.hasValidRatings(player2)) {
      const ratingSimilarity = this.calculateRatingSimilarity(player1, player2);
      score += ratingSimilarity * 0.1; // 10% weight
      factors += 0.1;
    }

    return factors > 0 ? score / factors : 0;
  }

  /**
   * Calculate string similarity using Levenshtein distance
   */
  static calculateStringSimilarity(str1, str2) {
    const matrix = [];
    const len1 = str1.length;
    const len2 = str2.length;

    if (len1 === 0) return len2 === 0 ? 1 : 0;
    if (len2 === 0) return 0;

    // Initialize matrix
    for (let i = 0; i <= len1; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= len2; j++) {
      matrix[0][j] = j;
    }

    // Fill matrix
    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1, // deletion
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j - 1] + cost, // substitution
        );
      }
    }

    const maxLen = Math.max(len1, len2);
    return 1 - matrix[len1][len2] / maxLen;
  }

  /**
   * Calculate rating similarity between two players
   */
  static calculateRatingSimilarity(player1, player2) {
    const categories = [
      "attitude",
      "skill",
      "physique",
      "mentality",
      "leadership",
      "professionalism",
    ];
    let totalDifference = 0;
    let validCategories = 0;

    categories.forEach((category) => {
      const rating1 = parseInt(player1[category]) || 0;
      const rating2 = parseInt(player2[category]) || 0;

      if (rating1 > 0 && rating2 > 0) {
        totalDifference += Math.abs(rating1 - rating2);
        validCategories++;
      }
    });

    if (validCategories === 0) return 0;

    const avgDifference = totalDifference / validCategories;
    const maxPossibleDifference = 5; // Max rating difference
    return 1 - avgDifference / maxPossibleDifference;
  }

  /**
   * 2. AUTO-CATEGORIZATION OF PLAYER TYPES
   * Categorizes players based on assessment patterns
   */
  static categorizePlayer(player) {
    if (!this.hasValidRatings(player)) {
      return {
        primaryType: "Unassessed",
        confidence: 0,
        description: "Insufficient rating data for categorization",
        recommendations: ["Complete full assessment ratings"],
      };
    }

    const ratings = this.extractRatings(player);
    const categories = this.analyzePlayerStrengths(ratings);

    return {
      primaryType: categories.primary.type,
      secondaryType: categories.secondary?.type || null,
      confidence: categories.primary.confidence,
      description: categories.primary.description,
      strengths: categories.strengths,
      weaknesses: categories.weaknesses,
      recommendations: this.generatePlayerRecommendations(ratings, categories),
      potentialRole: this.suggestPlayerRole(ratings, player.position),
      developmentFocus: this.suggestDevelopmentAreas(ratings),
    };
  }

  /**
   * Analyze player strengths and determine player type
   */
  static analyzePlayerStrengths(ratings) {
    const {
      attitude,
      skill,
      physique,
      mentality,
      leadership,
      professionalism,
    } = ratings;

    // Calculate category scores
    const technical = (skill + professionalism) / 2;
    const physical = physique;
    const mental = (mentality + attitude) / 2;

    const overall =
      (attitude + skill + physique + mentality + leadership + professionalism) /
      6;

    // Determine primary player type
    let primaryType = null;

    // Elite Prospect (high overall + balanced)
    if (
      overall >= 4.0 &&
      Math.min(
        attitude,
        skill,
        physique,
        mentality,
        leadership,
        professionalism,
      ) >= 3
    ) {
      primaryType = {
        type: "Elite Prospect",
        confidence: 0.9,
        description:
          "Exceptional all-around player with strong potential across all areas",
      };
    }
    // Technical Leader (high skill + leadership)
    else if (technical >= 4.0 && leadership >= 4.0) {
      primaryType = {
        type: "Technical Leader",
        confidence: 0.85,
        description:
          "Skilled player with strong leadership qualities and game understanding",
      };
    }
    // Physical Powerhouse (high physique + attitude)
    else if (physical >= 4.0 && attitude >= 3.5) {
      primaryType = {
        type: "Physical Powerhouse",
        confidence: 0.8,
        description:
          "Strong physical presence with good attitude and work ethic",
      };
    }
    // Mental Strategist (high mentality + professionalism)
    else if (mental >= 4.0 && professionalism >= 4.0) {
      primaryType = {
        type: "Mental Strategist",
        confidence: 0.8,
        description:
          "Intelligent player with excellent game reading and professional approach",
      };
    }
    // Skillful Technician (high skill, lower physical)
    else if (skill >= 4.0 && physical < 3.5) {
      primaryType = {
        type: "Skillful Technician",
        confidence: 0.75,
        description:
          "Technically gifted player who relies on skill over physical attributes",
      };
    }
    // Raw Talent (mixed ratings with high potential indicators)
    else if (overall >= 3.0 && (skill >= 4.0 || physical >= 4.0)) {
      primaryType = {
        type: "Raw Talent",
        confidence: 0.7,
        description:
          "Shows promise in key areas but needs development in others",
      };
    }
    // Solid Contributor (consistent moderate ratings)
    else if (overall >= 2.5 && Math.min(attitude, professionalism) >= 3.0) {
      primaryType = {
        type: "Solid Contributor",
        confidence: 0.65,
        description:
          "Reliable player with good attitude, suitable for squad development",
      };
    }
    // Development Project (low overall but good attitude)
    else if (attitude >= 3.5 && professionalism >= 3.0) {
      primaryType = {
        type: "Development Project",
        confidence: 0.6,
        description:
          "Good character foundation but requires significant technical development",
      };
    }
    // Limited Potential
    else {
      primaryType = {
        type: "Limited Potential",
        confidence: 0.5,
        description:
          "Below threshold for current development program requirements",
      };
    }

    // Identify top strengths and weaknesses
    const ratingArray = [
      { name: "Attitude", value: attitude },
      { name: "Skill", value: skill },
      { name: "Physique", value: physique },
      { name: "Mentality", value: mentality },
      { name: "Leadership", value: leadership },
      { name: "Professionalism", value: professionalism },
    ];

    const strengths = ratingArray
      .filter((r) => r.value >= 4.0)
      .map((r) => r.name);
    const weaknesses = ratingArray
      .filter((r) => r.value <= 2.0)
      .map((r) => r.name);

    return {
      primary: primaryType,
      secondary: null, // Could add secondary classification logic
      strengths,
      weaknesses,
      overall,
    };
  }

  /**
   * 3. SUGGESTED FOLLOW-UP ACTIONS
   * Generate intelligent suggestions based on player ratings and context
   */
  static generateFollowUpActions(player, gameContext = {}) {
    const actions = [];
    const ratings = this.extractRatings(player);
    const overall = this.calculateOverallRating(ratings);
    const category = this.categorizePlayer(player);

    // Priority level actions based on overall rating
    if (overall >= 4.0) {
      actions.push({
        priority: "High",
        action: "Immediate Contact",
        description:
          "Contact player/family within 24 hours to express interest",
        timeline: "24 hours",
        responsible: "Head Scout",
        icon: "phone",
      });

      actions.push({
        priority: "High",
        action: "Schedule Trial",
        description: "Arrange formal trial session with coaching staff",
        timeline: "1 week",
        responsible: "Academy Manager",
        icon: "calendar",
      });
    } else if (overall >= 3.0) {
      actions.push({
        priority: "Medium",
        action: "Additional Scouting",
        description: "Watch player in at least 2 more games before decision",
        timeline: "2-4 weeks",
        responsible: "Assigned Scout",
        icon: "eye",
      });

      actions.push({
        priority: "Medium",
        action: "Development Assessment",
        description: "Evaluate potential for improvement with coaching",
        timeline: "2 weeks",
        responsible: "Youth Coach",
        icon: "trending-up",
      });
    } else if (overall >= 2.0) {
      actions.push({
        priority: "Low",
        action: "Monitor Progress",
        description: "Keep on watch list, reassess in 6 months",
        timeline: "6 months",
        responsible: "Database Manager",
        icon: "clock",
      });
    } else {
      actions.push({
        priority: "Low",
        action: "Archive Record",
        description:
          "Player below current standards, archive for future reference",
        timeline: "Immediate",
        responsible: "Data Clerk",
        icon: "archive",
      });
    }

    // Specific category-based actions
    switch (category.primaryType) {
      case "Elite Prospect":
        actions.push({
          priority: "High",
          action: "Director Review",
          description: "Present to Academy Director for priority consideration",
          timeline: "48 hours",
          responsible: "Head Scout",
          icon: "star",
        });
        break;

      case "Technical Leader":
        actions.push({
          priority: "Medium",
          action: "Leadership Assessment",
          description: "Evaluate captain/leadership potential in trial setting",
          timeline: "1 week",
          responsible: "Youth Coach",
          icon: "users",
        });
        break;

      case "Development Project":
        actions.push({
          priority: "Medium",
          action: "Development Plan",
          description: "Create specific improvement pathway if trialed",
          timeline: "2 weeks",
          responsible: "Development Coach",
          icon: "map",
        });
        break;
    }

    // Position-specific actions
    if (player.position) {
      const positionActions = this.getPositionSpecificActions(
        player.position,
        ratings,
      );
      actions.push(...positionActions);
    }

    // Game context-based actions
    if (gameContext.gameImportance === "High") {
      actions.push({
        priority: "Medium",
        action: "Pressure Performance",
        description: "Note: Player performed in high-pressure environment",
        timeline: "Immediate",
        responsible: "Scout",
        icon: "shield",
      });
    }

    return actions.sort((a, b) => {
      const priorityOrder = { High: 3, Medium: 2, Low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }

  /**
   * 4. SCOUT WORKLOAD BALANCING
   * Analyze scout activity and suggest workload optimization
   */
  static analyzeScoutWorkload(scoutId, allGameReports, timeframe = 30) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - timeframe);

    const scoutReports = allGameReports.filter(
      (report) =>
        report.scoutId === scoutId && new Date(report.matchDate) >= cutoffDate,
    );

    const analysis = {
      gamesAttended: scoutReports.length,
      playersAssessed: 0,
      leadsGenerated: 0,
      averageRating: 0,
      travelDistance: 0,
      efficiency: 0,
      recommendations: [],
    };

    // Calculate metrics
    let totalRatings = 0;
    let ratingCount = 0;
    let totalTravel = 0;

    scoutReports.forEach((report) => {
      if (report.playersGameData) {
        analysis.playersAssessed += report.playersGameData.length;

        report.playersGameData.forEach((player) => {
          const overallRating = this.calculateOverallRating(
            this.extractRatings(player),
          );
          if (overallRating >= 3.0) analysis.leadsGenerated++;

          totalRatings += overallRating;
          ratingCount++;
        });
      }

      // Estimate travel distance (could be enhanced with actual GPS data)
      if (report.travelDistance) {
        totalTravel += report.travelDistance;
      }
    });

    analysis.averageRating = ratingCount > 0 ? totalRatings / ratingCount : 0;
    analysis.travelDistance = totalTravel;
    analysis.efficiency =
      analysis.gamesAttended > 0
        ? analysis.leadsGenerated / analysis.gamesAttended
        : 0;

    // Generate recommendations
    if (analysis.gamesAttended < 4) {
      analysis.recommendations.push(
        "Increase scouting frequency - aim for 4+ games per month",
      );
    }
    if (analysis.efficiency < 0.5) {
      analysis.recommendations.push(
        "Focus on higher quality games or different age groups",
      );
    }
    if (analysis.averageRating < 2.5) {
      analysis.recommendations.push(
        "Consider recalibrating rating standards with head scout",
      );
    }
    if (analysis.gamesAttended > 12) {
      analysis.recommendations.push(
        "High activity level - ensure quality assessment time per player",
      );
    }

    return analysis;
  }

  /**
   * 5. TRAVEL OPTIMIZATION
   * Suggest optimal travel routes for multi-game scouting
   */
  static optimizeTravelRoute(upcomingGames) {
    // This would integrate with mapping services in a real implementation
    // scoutLocation parameter would be used for distance calculations
    const optimizedRoute = {
      recommendedOrder: [],
      totalDistance: 0,
      estimatedTime: 0,
      costSavings: 0,
      suggestions: [],
    };

    // Sort games by geographic proximity and timing
    const gamesByRegion = this.groupGamesByRegion(upcomingGames);

    Object.keys(gamesByRegion).forEach((region) => {
      const regionGames = gamesByRegion[region];
      if (regionGames.length > 1) {
        optimizedRoute.suggestions.push({
          type: "Multi-game opportunity",
          description: `${regionGames.length} games in ${region} area`,
          games: regionGames,
          potentialSaving: "£15-25 travel costs",
        });
      }
    });

    return optimizedRoute;
  }

  /**
   * UTILITY METHODS
   */
  static hasValidRatings(player) {
    const categories = [
      "attitude",
      "skill",
      "physique",
      "mentality",
      "leadership",
      "professionalism",
    ];
    return categories.some((category) => {
      const rating = parseInt(player[category]);
      return !isNaN(rating) && rating > 0;
    });
  }

  static extractRatings(player) {
    return {
      attitude: parseInt(player.attitude) || 0,
      skill: parseInt(player.skill) || 0,
      physique: parseInt(player.physique) || 0,
      mentality: parseInt(player.mentality) || 0,
      leadership: parseInt(player.leadership) || 0,
      professionalism: parseInt(player.professionalism) || 0,
    };
  }

  static calculateOverallRating(ratings) {
    const values = Object.values(ratings);
    const validRatings = values.filter((rating) => rating > 0);
    return validRatings.length > 0
      ? validRatings.reduce((sum, rating) => sum + rating, 0) /
          validRatings.length
      : 0;
  }

  static getConfidenceLevel(similarity) {
    if (similarity >= 0.9) return "Very High";
    if (similarity >= 0.8) return "High";
    if (similarity >= 0.7) return "Medium";
    return "Low";
  }

  static getSuggestedDuplicateAction(similarity) {
    if (similarity >= 0.9) return "Likely duplicate - verify before proceeding";
    if (similarity >= 0.8) return "Review previous assessment before adding";
    return "Possible duplicate - check details manually";
  }

  static generatePlayerRecommendations(ratings, categories) {
    const recommendations = [];

    if (categories.overall >= 3.5) {
      recommendations.push("Strong candidate for trial invitation");
    }

    if (categories.strengths.includes("Leadership")) {
      recommendations.push("Consider for captaincy roles in trial games");
    }

    if (categories.weaknesses.length > 0) {
      recommendations.push(
        `Focus development on: ${categories.weaknesses.join(", ")}`,
      );
    }

    return recommendations;
  }

  static suggestPlayerRole(ratings, position) {
    // Position-specific role suggestions based on ratings
    const roleMap = {
      Goalkeeper: ["Shot Stopper", "Sweeper Keeper", "Distribution Specialist"],
      Defender: [
        "Defensive Anchor",
        "Ball-Playing Defender",
        "Attacking Full-back",
      ],
      Midfielder: ["Box-to-Box", "Playmaker", "Defensive Mid", "Attacking Mid"],
      Forward: [
        "Target Man",
        "Pace Merchant",
        "Clinical Finisher",
        "Creative Forward",
      ],
    };

    return roleMap[position] ? roleMap[position][0] : "Versatile Player";
  }

  static suggestDevelopmentAreas(ratings) {
    const areas = [];
    Object.entries(ratings).forEach(([category, rating]) => {
      if (rating < 3) {
        areas.push(category.charAt(0).toUpperCase() + category.slice(1));
      }
    });
    return areas;
  }

  static getPositionSpecificActions(position, ratings) {
    const actions = [];

    if (position === "Goalkeeper" && ratings.mentality >= 4) {
      actions.push({
        priority: "Medium",
        action: "Goalkeeper Assessment",
        description: "Arrange specialized goalkeeper coach evaluation",
        timeline: "1 week",
        responsible: "GK Coach",
        icon: "target",
      });
    }

    return actions;
  }

  static groupGamesByRegion(games) {
    const regions = {};
    games.forEach((game) => {
      const region = game.region || "Unknown";
      if (!regions[region]) regions[region] = [];
      regions[region].push(game);
    });
    return regions;
  }
}

export default AIInsightsService;
